import { motion } from "motion/react";
import { Mail, Zap, ShoppingCart, CheckCircle2, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";

export function RevenueRecoverySection() {
  const [activeFunnelStage, setActiveFunnelStage] = useState(0);

  const funnelStages = [
    { name: "Browse", count: "10,000", color: "#00F5FF", icon: "👀" },
    { name: "Cart", count: "3,000", color: "#FF4D00", icon: "🛒" },
    { name: "Checkout", count: "1,500", color: "#FF4D00", icon: "💳" },
    { name: "Abandoned", count: "900", color: "#FF4D00", icon: "😢" },
    { name: "Recovered", count: "270", color: "#10B981", icon: "✅" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFunnelStage((prev) => (prev + 1) % funnelStages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: ShoppingCart,
      title: "Form Builder Nativo & Popup Capture",
      description: "Crie formulários pop-ups com gatilhos precisos (Exit Intent, After X items) capturando leads antes deles saírem.",
      color: "#00F5FF",
    },
    {
      icon: Zap,
      title: "Webhooks & Email Automation",
      description: "Configure webhooks (Zapier/N8n) ou emails automáticos disparados no momento exato que o carrinho é detectado como 'Abandonado'.",
      color: "#FF4D00",
    },
    {
      icon: CheckCircle2,
      title: "Gestão de Carrinhos Avançada",
      description: "Veja carrinhos 'Active' e 'Abandoned' com filtros avançados. Você sabe quanto dinheiro está 'parado' na mesa.",
      color: "#10B981",
    },
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,77,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,77,0,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

      {/* Animated Glow */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(255,77,0,0.15), transparent 70%)",
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{ duration: 5, repeat: Infinity }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <AlertCircle className="w-8 h-8 text-[#FF4D00]" />
              <span className="font-['Roboto_Mono'] text-[#FF4D00] text-sm tracking-[0.3em] uppercase">
                Pare o Sangue
              </span>
            </div>
            <h2 className="font-['Sora'] text-4xl md:text-5xl font-bold text-white mb-6">
              Recuperação de Carrinhos
              <br />
              <span className="text-[#FF4D00] drop-shadow-[0_0_30px_rgba(255,77,0,0.5)]">
                e Automação Estratégica
              </span>
            </h2>
            <p className="font-['Inter'] text-xl text-gray-300 max-w-3xl mx-auto">
              A maioria das lojas perde{" "}
              <span className="text-[#FF4D00] font-bold text-2xl">70%</span> dos carrinhos.
              <br />
              O WP Total Tracker lhe dá as ferramentas para{" "}
              <span className="text-[#10B981] font-semibold">reduzir isso drasticamente</span>.
            </p>
          </motion.div>

          {/* Cart Funnel Animation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#0B0E14]/80 backdrop-blur-xl border border-[#FF4D00]/30 rounded-3xl p-12 mb-16"
            style={{
              boxShadow: "0 0 60px rgba(255,77,0,0.2), inset 0 0 80px rgba(255,77,0,0.05)",
            }}
          >
            <h3 className="font-['Sora'] text-2xl font-bold text-white text-center mb-10">
              Funil de Conversão: Do Browse ao Recovered
            </h3>

            <div className="flex items-center justify-center gap-4 mb-12">
              {funnelStages.map((stage, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Funnel Block */}
                  <motion.div
                    className="relative overflow-hidden rounded-lg transition-all duration-500"
                    style={{
                      width: `${160 - index * 20}px`,
                      height: "120px",
                      backgroundColor: activeFunnelStage === index ? `${stage.color}40` : `${stage.color}20`,
                      border: `2px solid ${activeFunnelStage === index ? stage.color : `${stage.color}40`}`,
                      boxShadow: activeFunnelStage === index ? `0 0 30px ${stage.color}60` : "none",
                    }}
                    animate={{
                      scale: activeFunnelStage === index ? 1.05 : 1,
                    }}
                  >
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                      <span className="text-3xl mb-2">{stage.icon}</span>
                      <span className="font-['Roboto_Mono'] text-xs text-gray-400 mb-1">
                        {stage.name}
                      </span>
                      <span className="font-['Sora'] font-bold text-white">
                        {stage.count}
                      </span>
                    </div>

                    {/* Active Pulse */}
                    {activeFunnelStage === index && (
                      <motion.div
                        className="absolute inset-0 rounded-lg"
                        style={{
                          border: `2px solid ${stage.color}`,
                        }}
                        animate={{
                          opacity: [0, 1, 0],
                          scale: [1, 1.1, 1],
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    )}
                  </motion.div>

                  {/* Arrow */}
                  {index < funnelStages.length - 1 && (
                    <div className="absolute top-1/2 -right-6 -translate-y-1/2 text-2xl text-gray-600">
                      →
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Automation Trigger Visual */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="flex items-center justify-center gap-8"
            >
              <div className="flex items-center gap-4 bg-[#FF4D00]/20 border border-[#FF4D00] rounded-xl px-6 py-4">
                <ShoppingCart className="w-8 h-8 text-[#FF4D00]" />
                <div>
                  <p className="font-['Roboto_Mono'] text-xs text-gray-400">CART STATUS</p>
                  <p className="font-['Sora'] text-lg font-bold text-[#FF4D00]">Abandoned</p>
                </div>
              </div>

              <motion.div
                className="flex items-center gap-2"
                animate={{
                  x: [0, 10, 0],
                }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <Zap className="w-8 h-8 text-[#00F5FF]" />
                <span className="font-['Roboto_Mono'] text-sm text-[#00F5FF]">AUTO TRIGGER</span>
              </motion.div>

              <div className="flex items-center gap-4 bg-[#10B981]/20 border border-[#10B981] rounded-xl px-6 py-4">
                <Mail className="w-8 h-8 text-[#10B981]" />
                <div>
                  <p className="font-['Roboto_Mono'] text-xs text-gray-400">ACTION</p>
                  <p className="font-['Sora'] text-lg font-bold text-[#10B981]">Email/Webhook Sent</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ scale: 1.05, y: -10 }}
                className="relative group"
              >
                <div
                  className="bg-[#0B0E14]/80 backdrop-blur-xl border rounded-2xl p-8 h-full transition-all duration-300"
                  style={{
                    borderColor: `${feature.color}40`,
                    boxShadow: `0 0 30px ${feature.color}15, inset 0 0 50px ${feature.color}05`,
                  }}
                >
                  {/* Icon */}
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundColor: `${feature.color}20`,
                      boxShadow: `0 0 20px ${feature.color}40`,
                    }}
                  >
                    <feature.icon className="w-8 h-8" style={{ color: feature.color }} />
                  </div>

                  {/* Title */}
                  <h3
                    className="font-['Sora'] text-xl font-bold mb-4"
                    style={{ color: feature.color }}
                  >
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="font-['Inter'] text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Hover Glow */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at center, ${feature.color}20, transparent 70%)`,
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* ROI Highlight */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-[#10B981]/20 to-[#00F5FF]/20 border-2 border-[#10B981] rounded-3xl p-12 text-center"
            style={{
              boxShadow: "0 0 60px rgba(16,185,129,0.3), inset 0 0 80px rgba(16,185,129,0.1)",
            }}
          >
            <h3 className="font-['Sora'] text-3xl font-bold text-white mb-4">
              Caso Real: Recuperação de 30% dos Carrinhos Abandonados
            </h3>
            <div className="grid md:grid-cols-3 gap-8 mt-8">
              <div>
                <p className="font-['Roboto_Mono'] text-sm text-gray-400 mb-2">CARRINHOS ABANDONADOS/MÊS</p>
                <p className="font-['Sora'] text-4xl font-bold text-[#FF4D00]">900</p>
              </div>
              <div>
                <p className="font-['Roboto_Mono'] text-sm text-gray-400 mb-2">TAXA DE RECUPERAÇÃO</p>
                <p className="font-['Sora'] text-4xl font-bold text-[#00F5FF]">30%</p>
              </div>
              <div>
                <p className="font-['Roboto_Mono'] text-sm text-gray-400 mb-2">RECEITA RECUPERADA/MÊS</p>
                <p className="font-['Sora'] text-4xl font-bold text-[#10B981]">R$ 45.000</p>
              </div>
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="font-['Inter'] text-xl text-gray-300 mt-8 max-w-2xl mx-auto"
            >
              O plugin se paga sozinho recuperando vendas perdidas.{" "}
              <span className="text-[#10B981] font-bold">ROI garantido.</span>
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
